import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import { RadioButtonGroup } from '../RadioButtonGroup';

interface Choice<Value extends string = string> {
  value: Value;
  label: NonNullable<ReactNode>;
  selected?: boolean;
}

const handleChange = <Value extends string = string>(
  value: Choice<Value>['value'],
  event: ChangeEvent<HTMLInputElement>,
  onChange: InputRadioGroupOnChange<Value>,
  onBlur: () => void,
  onFocus: () => void,
): void => {
  if (onFocus) {
    onFocus();
  }
  onChange(value, event);
  if (onBlur) {
    onBlur();
  }
};

type InputRadioGroupOnChange<Value extends string = string> = (
  value: Choice<Value>['value'],
  event: ChangeEvent<HTMLInputElement>,
) => void;

export interface InputRadioGroupProps<Value extends string>
  extends Omit<ComponentProps<'input'>, 'onChange' | 'onClick'> {
  choices: Choice<Value>[];
  id?: string;
  name: string;
  stretch?: boolean;
  value?: string;
  onChange: InputRadioGroupOnChange<Value>;
  onBlur: () => void;
  onFocus: () => void;
}

export function InputRadioGroup<Value extends string>({
  choices,
  id,
  name,
  stretch,
  value,
  onChange,
  onBlur,
  onFocus,
  ...props
}: InputRadioGroupProps<Value>): ReactNode {
  return (
    <RadioButtonGroup stretch={stretch}>
      {choices.map((choice) => (
        <RadioButtonGroup.RadioButton
          {...props}
          key={choice.value}
          checked={value === choice.value}
          value={choice.value}
          id={`${id || ''}-${choice.value}`}
          name={name}
          onChange={(e): void => handleChange(choice.value, e, onChange, onBlur, onFocus)}
        >
          {choice.label}
        </RadioButtonGroup.RadioButton>
      ))}
    </RadioButtonGroup>
  );
}
